import {
  ResponsiveTableContext,
  Controller2,
  makeController2,
  ResponsiveTable,
  Pagination,
} from '@apia/table';
import { action, makeObservable, observable, toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { FC } from 'react';
import { EventEmitter, TApiaFilter } from '@apia/util';
import { TableControllerEvents, TableControllerState } from './types';

let controllerCount = 0;

export class TableController {
  private emitter = new EventEmitter<TableControllerEvents>();
  private tableName = `TableController${controllerCount++}`;
  private tableSelectionController: Controller2;
  private controllerComponent: FC = () => null;

  state: TableControllerState = {
    columns: new Map(),
    filters: new Map(),
    pagination: { currentPage: 0, totalPages: 0, totalRecords: 0 },
    props: {},
    rows: [],
  };

  constructor(initialState?: Partial<TableControllerState>) {
    makeObservable(this, {
      state: observable,
      updateState: action,
    });

    const [controller, controllerComponent] = makeController2(this.tableName);
    this.tableSelectionController = controller;
    this.controllerComponent = controllerComponent;

    if (initialState) {
      Object.assign(this.state, initialState);
    }
  }

  public Context = observer(({ children }: { children: React.ReactNode }) => {
    const props = toJS(this.state.props);

    return (
      <ResponsiveTableContext
        {...props}
        columns={[...toJS(this.state.columns).values()]}
        rows={toJS(this.state.rows)}
        filters={[...toJS(this.state.filters).values()].map((c) => {
          return {
            id: c.filterState.id,
            column: c.filterState.column,
            asAdditional:
              c.filterState.isAdditional ||
              this.state.columns.get(c.filterState.column || '')?.hidden,
          } as TApiaFilter;
        })}
        FiltersRenderer={(filterId) =>
          this.state.filters.get(filterId)!.Component
        }
        name={this.tableName}
        SelectionHandler={this.controllerComponent}
        onSortChange={() => {
          this.emitter.emit.bind(this.emitter, 'onSortChange');
          // if (this.state.columns.get(c.name)?.currentSorting)
          // this.state.columns.get(c.name)?.currentSorting = 'A';
        }}
        onChangeSelection={this.emitter.emit.bind(
          this.emitter,
          'onChangeSelection',
        )}
        onFilterBlur={this.emitter.emit.bind(this.emitter, 'onFilterBlur')}
        onFilterChange={this.emitter.emit.bind(this.emitter, 'onFilterChange')}
        onFilterPressEnter={this.emitter.emit.bind(
          this.emitter,
          'onFilterPressEnter',
        )}
        onRowClick={this.emitter.emit.bind(this.emitter, 'onRowClick')}
        onSelectRows={this.emitter.emit.bind(this.emitter, 'onSelectRows')}
      >
        {children}
      </ResponsiveTableContext>
    );
  });

  public getTableName() {
    return this.tableName;
  }

  public Pagination = observer(() => {
    return (
      <Pagination
        currentPage={this.state.pagination.currentPage}
        pageCount={this.state.pagination.totalPages}
        recordsCount={this.state.pagination.totalRecords}
        onPageChange={this.emitter.emit.bind(this.emitter, 'onPage')}
        onRefresh={this.emitter.emit.bind(this.emitter, 'onRefresh', null)}
      />
    );
  });

  public Table = ResponsiveTable;

  public on<K extends keyof TableControllerEvents>(
    ev: K,
    cb: (e: TableControllerEvents[K]) => unknown,
  ) {
    return this.emitter.on(ev, cb);
  }

  public updateState(
    newState:
      | Partial<TableControllerState>
      | ((currentState: TableControllerState) => void),
  ) {
    if (newState instanceof Function) {
      newState(this.state);
    } else {
      Object.assign(this.state, newState);
    }
  }
}
